import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Sparkles } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface SearchSuggestionsProps {
  query: string;
  onSelectSuggestion: (suggestion: string) => void;
}

export function SearchSuggestions({ query, onSelectSuggestion }: SearchSuggestionsProps) {
  const { data: suggestions, isLoading } = useQuery({
    queryKey: ["search-suggestions", query],
    queryFn: async () => {
      if (query.length < 3) return [];

      const { data, error } = await supabase.functions.invoke("ai-search-suggestions", {
        body: { query },
      });

      if (error) throw error;
      return data.suggestions || [];
    },
    enabled: query.length >= 3,
    staleTime: 60000, // 1 minute
  });

  if (!query || query.length < 3) return null;
  if (isLoading) {
    return (
      <div className="space-y-2 p-2 border-t">
        <div className="flex items-center gap-2 px-2">
          <Sparkles className="h-3 w-3 text-primary" />
          <span className="text-xs text-muted-foreground">AI suggestions</span>
        </div>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div className="space-y-2 p-2 border-t">
      <div className="flex items-center gap-2 px-2">
        <Sparkles className="h-3 w-3 text-primary" />
        <span className="text-xs text-muted-foreground">AI suggestions</span>
      </div>
      {suggestions.map((suggestion: string, idx: number) => (
        <div
          key={idx}
          className="p-2 hover:bg-accent rounded-md cursor-pointer text-sm"
          onClick={() => onSelectSuggestion(suggestion)}
        >
          {suggestion}
        </div>
      ))}
    </div>
  );
}
